import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import axiosInstance from "../../utils/axios";
import { entitiesConfig } from "../../components/templateComponents/constants";
import "./BothFloorNeighborsTable.css";


const BothFloorsNeighborsTable = ({
  connections = [],
  selectedFloorId,
  onAddRow,
  onUpdateConnection,
  onDeleteConnection,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editingField, setEditingField] = useState("");
  const [editValue, setEditValue] = useState("");
  const [originalValue, setOriginalValue] = useState("");
  const [tableData, setTableData] = useState([]);
  const [saving, setSaving] = useState(false);

  const buildingId = localStorage.getItem("selectedBuildingId") || "";
  const saveEndpoint = entitiesConfig.navigationMapping.saveEndpoint;

  const handleEdit = (id, field, value) => {
    if (editingId === id && editingField === field) return;
    setEditingId(id);
    setEditingField(field);
    const stringValue =
      value !== null && value !== undefined ? String(value) : "";
    setEditValue(stringValue);
    setOriginalValue(stringValue);
  };

  const handleSave = (id) => {
    if (!onUpdateConnection || !editingField) return;
    let saveValue = editValue;
    const isNumericField = [
      "distance_meters",
      "nav_directions_id",
      "level",
    ].includes(editingField);
    if (isNumericField) {
      const numValue = Number(editValue);
      saveValue = isNaN(numValue) ? 0 : numValue;
    }
    if (editingField === "source_node") {
      onUpdateConnection(id, "from", saveValue);
    } else if (editingField === "neighbour_node") {
      onUpdateConnection(id, "to", saveValue);
    } else {
      onUpdateConnection(id, editingField, saveValue);
    }
    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === id
          ? {
              ...row,
              [editingField]: saveValue,
              ...(editingField === "from" ? { source_node: saveValue } : {}),
              ...(editingField === "to" ? { neighbour_node: saveValue } : {}),
            }
          : row
      )
    );
    setEditingId(null);
    setEditingField("");
    setEditValue("");
    setOriginalValue("");
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditingField("");
    setEditValue("");
    setOriginalValue("");
  };

  const handleChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleKeyDown = (e, id) => {
    if (e.key === "Enter") handleSave(id);
    else if (e.key === "Escape") handleCancel();
  };

  const handleAddNewRow = () => {
    // if (onAddRow) onAddRow();
  };

  const handleDownload = () => {
    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BothFloorsNeighbors");
    XLSX.writeFile(workbook, "BothFloorsNeighbors.xlsx");
  };

  // ✅ Save all table data to DB as array in single call
  const handleSaveToDB = async () => {
    setSaving(true);
    try {
      const payload = tableData.map((row) => ({
        bldg_fid: row.bec_floor_id,
        bldg_id: buildingId,
        source_node: Number(row.source_node),
        neighbour_node: Number(row.neighbour_node),
        nav_directions_id: Number(row.nav_directions_id),
        level: Number(row.level),
        distance_meters: Number(row.distance_meters),
      }));

     const response = await axiosInstance.post(saveEndpoint, payload);
      if (response.data?.Status === "Success") {
        alert("All rows saved successfully to DB!");
      } else {
        alert("Failed to save rows to DB. Check API response.");
      }
    } catch (error) {
      console.error("Error saving to DB:", error);
      alert("Error saving to DB.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!selectedFloorId) {
      setTableData([]);
      return;
    }
    const initialData = connections.map((conn) => ({
      id: conn.id,
      bec_floor_id: selectedFloorId,
      building_id: buildingId,
      source_node: conn.from,
      neighbour_node: conn.to,
      distance_meters: conn.distance_meters || 10,
      nav_directions_id: conn.nav_directions_id || 2,
      level: conn.level || 1,
    }));
    setTableData(initialData);
  }, [connections, selectedFloorId, buildingId]);

  const handleDelete = (id) => {
    if (onDeleteConnection) onDeleteConnection(id);
  };

  if (!tableData || tableData.length === 0) {
    return (
      <div className="neighbors-table-empty">
        <p>No connections made yet.</p>
      </div>
    );
  }

  return (
    <div className="neighbors-table-container">
      <div className="neighbors-table-header">
        <h3 className="neighbors-table-title">Both Floors Neighbors Table</h3>
        <div className="neighbors-table-actions">
          <button
            onClick={handleAddNewRow}
            className="add-row-btn"
            title="Add New Row"
          >
            <span className="icon-plus" /> Add Row
          </button>
          <button
            onClick={handleDownload}
            className="download-excel-btn"
            title="Download as Excel"
          >
            <span className="icon-download" />
          </button>
        </div>
      </div>
      <div className="neighbors-table-scroll">
        <table className="neighbors-table">
          <thead>
            <tr>
              <th>bec_floor_id</th>
              <th>building_id</th>
              <th>source_node</th>
              <th>neighbour_node</th>
              <th>distance_meters</th>
              <th>nav_directions_id</th>
              <th>level</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row) => (
              <tr key={row.id} className="neighbors-table-row">
                {[
                  "bec_floor_id",
                  "building_id",
                  "source_node",
                  "neighbour_node",
                  "distance_meters",
                  "nav_directions_id",
                  "level",
                ].map((field) => (
                  <td key={field} className="neighbors-table-cell">
                    {editingId === row.id && editingField === field ? (
                      <input
                        type={
                          [
                            "distance_meters",
                            "nav_directions_id",
                            "level",
                          ].includes(field)
                            ? "number"
                            : "text"
                        }
                        value={editValue}
                        onChange={handleChange}
                        onKeyDown={(e) => handleKeyDown(e, row.id)}
                        className="cell-input"
                        autoFocus
                        min={
                          ["nav_directions_id", "level"].includes(field) ? 1 : 0
                        }
                        step={["distance_meters"].includes(field) ? 0.1 : 1}
                      />
                    ) : (
                      <span
                        onClick={() => handleEdit(row.id, field, row[field])}
                        className="cell-value"
                      >
                        {row[field]}
                      </span>
                    )}
                  </td>
                ))}
                <td className="neighbors-table-actions-cell">
                  {editingId === row.id ? (
                    <div className="action-group">
                      <button
                        onClick={() => handleSave(row.id)}
                        className="action-btn save"
                        title="Save"
                      >
                        ✔
                      </button>
                      <button
                        onClick={handleCancel}
                        className="action-btn cancel"
                        title="Cancel"
                      >
                        ✖
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setEditingId(row.id);
                          setEditingField("source_node");
                          setEditValue(row["source_node"] || "");
                        }}
                        className="action-btn edit"
                        title="Edit"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => handleDelete(row.id)}
                        className="action-btn delete"
                        title="Delete"
                      >
                        🗑
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ✅ Save to DB Button */}
      <div className="neighbors-table-actions" style={{ marginTop: "16px" }}>
        <button
          onClick={handleSaveToDB}
          className="save-db-btn"
          disabled={saving}
          style={{
            background: "#10b981",
            color: "#fff",
            padding: "8px 16px",
            border: "none",
            borderRadius: "6px",
          }}
        >
          {saving ? "Saving..." : "Save to DB"}
        </button>
      </div>
    </div>
  );
};

export default BothFloorsNeighborsTable;
